(() => {
  if (window.akaProductHoverZoomLoaded) return;
  window.akaProductHoverZoomLoaded = true;
  const desktop = matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const instances = new Map();
  let gallerySequence = 0;
  const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

  class ProductHoverZoom {
    constructor(gallery) {
      this.gallery = gallery;
      this.custom = !!gallery.closest('.aka-product');
      this.root = gallery.closest('.aka-product, product-info, .product') || gallery.parentElement;
      this.surface = gallery.querySelector('.aka-media__stage, [id^="GalleryViewer-"]') || gallery;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.panel = document.createElement('div');
      this.panel.className = 'aka-hover-zoom-panel';
      this.panel.id = `AkaProductZoom-${++gallerySequence}`;
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.hidden = true;
      this.lens = document.createElement('div');
      this.lens.className = 'aka-hover-zoom-lens';
      this.lens.setAttribute('aria-hidden', 'true');
      this.lens.hidden = true;
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'aka-hover-zoom-toggle';
      this.button.textContent = 'Zoom image';
      this.button.setAttribute('aria-label', 'Zoom product image. Use arrow keys to pan; Escape to close.');
      this.button.setAttribute('aria-expanded', 'false');
      this.button.setAttribute('aria-controls', this.panel.id);
      this.surface.dataset.akaHoverZoom = '';
      this.surface.append(this.button);
      // Fixed overlays stay outside gallery clipping and transformed section ancestors.
      document.body.append(this.panel, this.lens);
      this.gallery.addEventListener('pointermove', event => this.pointer(event), options);
      this.gallery.addEventListener('pointerleave', () => { if (!this.pinned) this.close(); }, options);
      this.gallery.addEventListener('pointerdown', event => { if (event.target !== this.button) this.close(); }, options);
      this.gallery.addEventListener('load', () => this.sync(), { ...options, capture: true });
      this.button.addEventListener('click', () => this.toggle(), options);
      this.button.addEventListener('keydown', event => this.key(event), options);
      this.button.addEventListener('blur', () => this.close(), options);
      window.addEventListener('scroll', () => this.close(), { ...options, capture: true, passive: true });
      window.addEventListener('resize', () => { this.close(); this.sync(); }, options);
      window.addEventListener('blur', () => this.close(), options);
      document.addEventListener('keydown', event => { if (event.key === 'Escape') this.close(); }, options);
      desktop.addEventListener('change', () => { this.close(); this.sync(); }, options);
      this.observer = new MutationObserver(() => {
        if (this.source && !this.images().includes(this.source)) this.close();
        else if (this.source && this.originalURL !== this.source.currentSrc) this.close();
        this.sync();
      });
      this.observer.observe(gallery, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'class', 'src', 'srcset'] });
      this.sync();
    }

    images() {
      const selector = this.custom
        ? '.aka-media__item:not([hidden]) > .aka-media__image, :scope > .aka-media__image'
        : '.product__modal-opener--image .product__media > img';
      return [...this.gallery.querySelectorAll(selector)].filter(image => {
        const rect = image.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && !image.closest('[hidden]') && image.complete && image.naturalWidth > 0;
      });
    }

    sync() {
      const hidden = !desktop.matches || this.images().length === 0;
      if (this.button.hidden !== hidden) this.button.hidden = hidden;
    }

    bounds(image) {
      const box = image.getBoundingClientRect();
      const style = getComputedStyle(image);
      let width = box.width, height = box.height;
      if (['contain', 'cover', 'scale-down', 'none'].includes(style.objectFit)) {
        const scale = style.objectFit === 'cover' ? Math.max(box.width / image.naturalWidth, box.height / image.naturalHeight)
          : style.objectFit === 'none' ? 1 : Math.min(box.width / image.naturalWidth, box.height / image.naturalHeight, style.objectFit === 'scale-down' ? 1 : Infinity);
        width = image.naturalWidth * scale;
        height = image.naturalHeight * scale;
      }
      const position = style.objectPosition.split(' ').map(value => value.endsWith('%') ? parseFloat(value) / 100 : 0.5);
      const left = box.left + (box.width - width) * (position[0] ?? 0.5);
      const top = box.top + (box.height - height) * (position[1] ?? 0.5);
      return { left, top, width, height, visible: { left: Math.max(left, box.left), top: Math.max(top, box.top), right: Math.min(left + width, box.right), bottom: Math.min(top + height, box.bottom) } };
    }

    pointer(event) {
      if (!desktop.matches || event.pointerType !== 'mouse') return;
      const control = event.target.closest('button, a, video, iframe, model-viewer');
      if (control && !control.matches('.product__media-toggle')) { if (!this.pinned) this.close(); return; }
      const image = this.images().find(item => {
        const box = this.bounds(item).visible;
        return event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
      });
      if (!image) { if (!this.pinned) this.close(); return; }
      this.open(image, { x: event.clientX, y: event.clientY });
    }

    open(image, point) {
      this.point = point;
      if (this.source === image && this.large) { this.schedule(); return; }
      if (this.source === image) return;
      this.close(false);
      this.source = image;
      this.originalURL = image.currentSrc || image.src;
      const revision = this.revision;
      const large = new Image();
      large.alt = '';
      large.decoding = 'async';
      const url = new URL(this.originalURL, location.href);
      // Theme assets already use their original resolution. Keep their exact versioned URL:
      // requesting a new width can return a stale cached rendition of a replaced asset.
      if (!url.pathname.includes('/assets/') && (url.hostname === 'cdn.shopify.com' || url.pathname.includes('/cdn/shop/'))) {
        url.searchParams.set('width', '3000');
        url.searchParams.delete('height');
      }
      let fallback = false;
      large.onload = () => {
        if (revision !== this.revision || this.source !== image || !desktop.matches) return;
        this.large = large;
        this.panel.replaceChildren(large);
        this.schedule();
      };
      large.onerror = () => {
        if (revision !== this.revision) return;
        if (!fallback && url.href !== this.originalURL) { fallback = true; large.src = this.originalURL; }
        else this.close();
      };
      large.src = url.href;
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.render(); });
    }

    render() {
      if (!desktop.matches || !this.source || !this.large || !this.images().includes(this.source)) { this.close(); return; }
      const box = this.bounds(this.source);
      const buy = this.root.querySelector('.aka-buy, .product__info-wrapper');
      const buyBox = buy?.getBoundingClientRect();
      const rightStart = Math.max(box.visible.right + 16, buyBox?.left > box.visible.right ? buyBox.left : 0);
      const leftEnd = Math.min(box.visible.left - 16, buyBox && buyBox.right < box.visible.left ? buyBox.right : innerWidth);
      const useLeft = (buyBox && buyBox.right <= box.visible.left) || (innerWidth - rightStart - 16 < 200 && leftEnd - 16 >= 200);
      const available = useLeft ? leftEnd - 16 : innerWidth - rightStart - 16;
      if (available < 200) { this.close(); return; }
      const zoom = 3;
      const width = Math.min(buyBox?.width || available, available, box.width * zoom);
      const left = useLeft ? leftEnd - width : rightStart;
      const height = Math.min(Math.max(box.visible.bottom - box.visible.top, 260), innerHeight - 32, box.height * zoom);
      if (height < 80 || width < 100) { this.close(); return; }
      const top = clamp(box.visible.top, 16, innerHeight - height - 16);
      const lensWidth = Math.min(width / zoom, box.visible.right - box.visible.left);
      const lensHeight = Math.min(height / zoom, box.visible.bottom - box.visible.top);
      const lensLeft = clamp(this.point.x - lensWidth / 2, box.visible.left, box.visible.right - lensWidth);
      const lensTop = clamp(this.point.y - lensHeight / 2, box.visible.top, box.visible.bottom - lensHeight);
      Object.assign(this.panel.style, { left: `${left}px`, top: `${top}px`, width: `${width}px`, height: `${height}px` });
      Object.assign(this.lens.style, { left: `${lensLeft}px`, top: `${lensTop}px`, width: `${lensWidth}px`, height: `${lensHeight}px` });
      Object.assign(this.large.style, { width: `${box.width * zoom}px`, height: `${box.height * zoom}px`, transform: `translate(${-Math.max(0, lensLeft - box.left) * zoom}px, ${-Math.max(0, lensTop - box.top) * zoom}px)` });
      this.panel.hidden = false;
      this.lens.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
    }

    toggle() {
      if (this.pinned) { this.close(); return; }
      if (!desktop.matches) return;
      const image = this.images().find(item => { const rect = item.getBoundingClientRect(); return rect.bottom > 0 && rect.top < innerHeight; });
      if (!image) return;
      const box = this.bounds(image).visible;
      this.open(image, { x: (box.left + box.right) / 2, y: (box.top + box.bottom) / 2 });
      this.pinned = true;
    }

    key(event) {
      if (!this.pinned || !this.source) return;
      const directions = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
      if (!directions[event.key]) return;
      event.preventDefault();
      event.stopPropagation();
      const box = this.bounds(this.source).visible;
      this.point.x = clamp(this.point.x + directions[event.key][0] * 35, box.left, box.right);
      this.point.y = clamp(this.point.y + directions[event.key][1] * 35, box.top, box.bottom);
      if (this.large) this.schedule();
    }

    close(reset = true) {
      this.revision = (this.revision || 0) + 1;
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.source = null;
      this.large = null;
      this.panel.hidden = this.lens.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      if (reset) this.pinned = false;
    }

    destroy() {
      this.close();
      this.controller.abort();
      this.observer.disconnect();
      this.button.remove(); this.panel.remove(); this.lens.remove();
      delete this.surface.dataset.akaHoverZoom;
    }
  }

  let scanFrame;
  const scan = () => {
    scanFrame = 0;
    for (const [gallery, instance] of instances) if (!gallery.isConnected) { instance.destroy(); instances.delete(gallery); }
    document.querySelectorAll('main .aka-product .aka-media, main media-gallery').forEach(gallery => {
      if (!instances.has(gallery)) instances.set(gallery, new ProductHoverZoom(gallery));
    });
  };
  const scheduleScan = () => { if (!scanFrame) scanFrame = requestAnimationFrame(scan); };
  new MutationObserver(scheduleScan).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', scheduleScan);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
})();
